// stores/userStore.js
import { defineStore } from 'pinia'
import { formatDateTime } from '@/components/logics/dateUtil'
import { apiService } from '@/services/api'
import Webpass from '@laragear/webpass'

export const useUserStore = defineStore('user', {
  state: () => ({
    currentUser: null,
    users: [],
    tableList: [],
    formData: null,
    otpSecret: null,
    otpQRCode: null,
    isUploadingIcon: false,
    formInit: {
      id: null,
      // userId: null,
      lastName: '',
      firstName: '',
      lastKana: '',
      firstKana: '',
      shortName: '',
      email: '',
      role: 'user',
      password: '',
      division: '',
      divisionKana: '',
      title: '',
      notifications: {
        electronicApplication: false,
        subsidy: false,
        agreement: false,
      },
      isOtpEnabled: false,
      isPasswordSigninEnabled: true,
      passkeys: [],
      iconPath: null,
    },
    roleOptions: [
      { label: '一般', value: 'user' },
      { label: '管理者', value: 'manager' },
      { label: '契約者', value: 'root' },
    ],
    currentRoleOptions: [],
  }),
  actions: {
    // ログイン
    login() {
      // ログイン後にユーザー情報を取得
      this.getCurrentUser()
    },

    // テーブル表示用のリストを初期化
    initTableList() {
      const baseColumns = [
        { label: 'No', key: 'rowNumber', data: (item, value) => value },
        {
          label: '氏名',
          key: 'name',
          data: (item, value) => value,
        },
        { label: '略称', key: 'shortName', data: (item, value) => value || '-' },
        {
          label: '権限',
          key: 'role',
          data: (item, value) => {
            return this.roleOptions.find(option => option.value === value)?.label || value || '-'
          },
        },
        {
          label: 'OTP認証',
          key: 'otpEnabled',
          data: (item, value) => (value ? '有効' : '無効'),
        },
        {
          label: '最終ログイン日時',
          key: 'lastLoginAt',
          data: (item, value) => {
            // lastLoginAtがなく、isVerifiedがfalseで、一般ユーザーでない場合にボタンを表示
            if (this.shouldShowResendEmailButton(item)) {
              return '認証メール再送信'
            }
            return formatDateTime(value) || '-'
          },
          action: (table, item, event) => {
            // 親コンポーネントで処理するため、何もしない
            event.stopPropagation()
          },
          actionEnabled: (table, item) => this.shouldShowResendEmailButton(item), // getterを使用した条件判定
          css: item => (this.shouldShowResendEmailButton(item) ? 'btn' : ''), // getterを使用した条件判定
        },
        // メールアドレス変更列を常に追加（表示条件は各アイテムで制御）
        {
          label: 'メールアドレス変更',
          key: 'changeEmail',
          data: (item, value) => {
            return this.shouldShowChangeEmailButton(item) ? 'メールアドレス変更' : '-'
          },
          action: (table, item, event) => {
            // 親コンポーネントで処理するため、何もしない
            event.stopPropagation()
          },
          actionEnabled: (table, item) => this.shouldShowChangeEmailButton(item), // getterを使用した条件判定
          css: item => (this.shouldShowChangeEmailButton(item) ? 'btn' : ''), // getterを使用した条件判定
        },
      ]

      this.tableList = baseColumns
    },

    // フォームデータの初期化
    async setFormData(id) {
      // オプションの取得
      this.currentRoleOptions = this.getRoleOptions()

      if (id) {
        // 編集の場合
        const user = await this.getUser(id)
        if (user) {
          this.formData = JSON.parse(JSON.stringify(user))
          return
        }
      }

      // 新規作成の場合
      this.formData = JSON.parse(JSON.stringify(this.formInit))
    },

    // ログイン中のユーザーを取得
    async getCurrentUser() {
      if (this.currentUser) {
        return true
      }
      try {
        const response = await apiService.get('/api/user/current')
        this.currentUser = response.user
        return true
      } catch (error) {
        return false
      }
    },

    // ユーザー一覧を取得
    async getUsers() {
      try {
        const response = await apiService.get('/api/users')
        this.users = response.users
        return true
      } catch (error) {
        this.users = []
        return false
      }
    },

    // ユーザーの情報を取得
    async getUser(id) {
      try {
        const response = await apiService.get(`/api/users/${id}`)
        // 通知メールを文字列からオブジェクトに変換
        if (typeof response.user.notifications === 'string') {
          response.user.notifications = JSON.parse(response.user.notifications)
        } else if (!response.user.notifications) {
          // null/undefinedの場合も初期値に設定
          this.setEmailNotificationDefault()
        }
        // isPasswordSigninEnabledをboolean値に変換
        if (response.user.isPasswordSigninEnabled !== undefined) {
          response.user.isPasswordSigninEnabled = Boolean(response.user.isPasswordSigninEnabled === true || response.user.isPasswordSigninEnabled === 'true')
        }
        // パスキーをオブジェクトに変換
        if (response.user.passkeys) {
          response.user.passkeys = response.user.passkeys.map(passkey => {
            return {
              id: passkey.id,
              name: passkey.alias,
              createdAt: formatDateTime(passkey.createdAt),
            }
          })
        } else {
          response.user.passkeys = []
        }
        return response.user
      } catch (error) {
        return false
      }
    },

    // ユーザー情報を保存（新規作成・更新）
    async saveUser() {
      try {
        const response = await apiService.post('/api/users', this.formData)
        if (this.isCurrentUser(this.formData.id)) {
          // 自分自身の情報を更新した場合、currentUserを更新
          this.currentUser = JSON.parse(JSON.stringify(this.formData))
        }
        await this.getUsers()
        return response.message
      } catch (error) {
        return null
      }
    },

    // ユーザーを削除
    async deleteUser() {
      try {
        const response = await apiService.delete(`/api/users/${this.formData.id}`)
        await this.getUsers()
        return response.message
      } catch (error) {
        return null
      }
    },

    // パスワード変更
    async changePassword(currentPassword, newPassword) {
      const data = {
        id: this.formData.id,
        currentPassword: currentPassword,
        newPassword: newPassword,
      }
      try {
        const response = await apiService.post('/api/users/change-password', data)
        return response.message
      } catch (error) {
        return null
      }
    },

    // パスワードリセット
    async resetPassword(password) {
      const data = {
        password: password,
      }
      try {
        const response = await apiService.post('/api/dashboard/reset-password', data)
        this.currentUser.isPasswordResetRequired = false
        return response.message
      } catch (error) {
        return null
      }
    },

    // ユーザーアイコンをアップロード
    async uploadUserIcon(userId, iconFile) {
      this.isUploadingIcon = true

      try {
        const formData = new FormData()
        formData.append('icon', iconFile)
        formData.append('id', this.formData.id)

        const response = await apiService.postFormData(`/api/users/${this.formData.id}/icon`, formData, {
          showErrorDialog: false, // エラーダイアログは表示しない
        })

        // ユーザー情報を更新
        if (this.isCurrentUser(this.formData.id)) {
          // 自分自身の場合はcurrentUserも更新
          this.currentUser.iconPath = response.iconPath
        }

        // formDataが現在のユーザーの場合は更新
        this.formData.iconPath = response.iconPath

        return 'アップロードに成功しました'
      } catch (error) {
        return false
      } finally {
        this.isUploadingIcon = false
      }
    },

    // ユーザーアイコンを削除
    async deleteUserIcon(userId) {
      try {
        const response = await apiService.delete(`/api/users/${userId}/icon`)

        // ユーザー情報を更新
        if (this.isCurrentUser(userId)) {
          // 自分自身の場合はcurrentUserも更新
          this.currentUser.iconPath = null
        }

        // formDataが現在のユーザーの場合は更新
        if (this.formData && this.formData.id === userId) {
          this.formData.iconPath = null
        }

        return response.message
      } catch (error) {
        return null
      }
    },

    // IDでユーザーを検索
    getUserById(id) {
      return this.users.find(user => user.id === Number(id))
    },

    // ユーザーコピー（新規作成時の雛形として）
    copyUser() {
      this.formData.userId = null
      this.formData.email = ''
      this.formData.password = ''
      this.formData.isOtpEnabled = false
      this.formData.passkeys = []
      this.formData.isPasswordResetRequired = true

      // コピー元がrootユーザーの場合はmanagerに変更
      if (this.formData.role === 'root') {
        this.formData.role = 'manager'
      }
    },

    // パスキー登録関数
    async registerPasskey(password, passkeyName) {
      // ユーザーデータのバックアップを作成
      const usersBackup = JSON.parse(JSON.stringify(this.users))
      const formDataBackup = JSON.parse(JSON.stringify(this.formData))

      try {
        // パスワード検証
        const passwordVerified = await this.verifyPassword(password)
        if (!passwordVerified) {
          return null
        }

        // WebAuthnの登録処理
        const { success, error } = await Webpass.attest('/webauthn/register/options', {
          path: '/webauthn/register',
          body: { alias: passkeyName },
        })

        if (!success) {
          return null
        }

        // ユーザーデータを取得しなおし
        this.formData = await this.getUser(this.formData.id)

        // テーブル用のユーザーリストも更新
        await this.getUsers()

        return 'パスキーが登録されました'
      } catch (error) {
        // エラー時はバックアップから復元
        this.users = usersBackup
        this.formData = formDataBackup

        await this.getUsers()
        return null
      }
    },

    // パスワード検証用のヘルパー関数（既存の関数がある場合はそれを使用）
    async verifyPassword(password) {
      try {
        const response = await apiService.post('/api/user/verify-password', {
          password: password,
        })

        return true
      } catch (error) {
        return false
      }
    },

    // パスキーの削除
    async deletePasskey(passkeyId) {
      try {
        const response = await apiService.delete(`/api/user/passkey/${passkeyId}`)

        // ユーザーデータを取得しなおし
        this.formData = await this.getUser(this.formData.id)

        // テーブル用のユーザーリストも更新
        await this.getUsers()

        return response.message
      } catch (error) {
        return null
      }
    },

    // 通知設定の更新 TODO
    async updateNotifications(notifications) {
      try {
        const response = await apiService.post('/api/users/notifications', {
          id: this.formData.id,
          notifications,
        })
        if (this.isCurrentUser(this.formData.id)) {
          this.currentUser.notifications = notifications
        }
        return response.message
      } catch (error) {
        return null
      }
    },

    // ログアウト
    async logout() {
      try {
        await apiService.post('/api/logout')
        this.currentUser = null
      } catch (error) {}
    },

    // 利用規約同意 TODO
    async agreeToTerms(revision) {
      try {
        const response = await apiService.post('/api/dashboard/agree-terms', { revision: revision })
        this.currentUser.agreedTermsRevision = revision
        return response.message
      } catch (error) {
        return null
      }
    },

    // OTPのシークレットキーとQRコードを取得
    async getOtpData() {
      try {
        const response = await apiService.get(`/api/user/${this.currentUser.id}/otp`)
        this.otpSecret = response.secret
        this.otpQRCode = response.qrCode
      } catch (error) {
        return null
      }
    },

    // OTPの有効化
    async enableOTP(otpCode) {
      try {
        const response = await apiService.post('/api/user/otp/enable', {
          id: this.currentUser.id,
          code: otpCode,
          secret: this.otpSecret,
        })
        this.currentUser.isOtpEnabled = true
        return response.message
      } catch (error) {
        return null
      }
    },

    // OTPの無効化
    async disableOTP() {
      try {
        const response = await apiService.delete(`/api/user/${this.formData.id}/otp`)
        this.formData = await this.getUser(this.formData.id)

        return response.message
      } catch (error) {
        return null
      }
    },

    // 権限チェック
    isManager() {
      return this.currentUser && this.currentUser.role === 'manager'
    },
    isRoot() {
      return this.currentUser && this.currentUser.role === 'root'
    },
    // 管理権限があるかどうか
    isAdmin() {
      return this.currentUser && (this.currentUser.role === 'manager' || this.currentUser.role === 'root')
    },
    // 指定したIDのユーザーが自分自身かどうか
    isCurrentUser(id) {
      return this.currentUser && this.currentUser.id === id
    },
    // ユーザーが自分自身の情報を表示しているかどうか
    isShowingCurrentUser() {
      return this.currentUser && this.formData && this.currentUser.id === this.formData.id
    },
    // メール通知を初期値に設定
    setEmailNotificationDefault() {
      if (this.formData) {
        this.formData.notifications = {
          electronicApplication: false,
          subsidy: false,
          agreement: false,
        }
      }
    },
    // 権限のオプションを取得
    getRoleOptions() {
      // 表示中のユーザーがrootの場合のみ、root権限を含む全てのオプションを表示
      if (this.formData && this.formData.role === 'root') {
        return this.roleOptions
      }

      // それ以外の場合は、ログイン中のユーザーの権限に関係なくroot権限は除外
      // （自分がrootユーザーでも、他のユーザーをrootにすることはできない）
      if (this.currentUser) {
        if (this.currentUser.role === 'user') {
          return this.roleOptions.filter(option => option.value === 'user')
        } else {
          // manager, root, その他の権限の場合でもroot権限は除外
          return this.roleOptions.filter(option => option.value !== 'root')
        }
      }
      return this.roleOptions.filter(option => option.value !== 'root')
    },
    // 現在の利用規約に同意しているかどうか
    isAgreedToTerms(revision) {
      return this.currentUser && this.currentUser.agreedTermsRevision < revision
    },
    // パスワードリセットが必要かどうか
    isPasswordResetRequired() {
      return this.currentUser && this.currentUser.isPasswordResetRequired
    },
    // 表示中のユーザーの権限を取得
    getCurrentUserRole() {
      if (this.formData) {
        const role = this.roleOptions.find(option => option.value === this.formData.role)
        return role ? role.label : this.formData.role
      }
      return ''
    },

    // メール認証メールを送信
    async sendVerificationEmail(userId) {
      try {
        const response = await apiService.post('/api/users/send-verification', {
          id: userId,
        })
        return response.message
      } catch (error) {
        return null
      }
    },

    // メールアドレス変更処理
    async changeUserEmail(userId, newEmail) {
      try {
        const response = await apiService.post('/api/users/change-email-request', {
          id: userId,
          newEmail: newEmail,
        })
        return response.message
      } catch (error) {
        return null
      }
    },
  },
  getters: {
    // フルネームを取得
    fullName: state => {
      if (!state.currentUser) return ''
      return `${state.currentUser.lastName} ${state.currentUser.firstName}`
    },
    // 権限名を取得
    roleName: state => {
      if (!state.currentUser) return ''
      const role = state.roleOptions.find(option => option.value === state.currentUser.role)
      return role ? role.label : state.currentUser.role
    },
    // OTP認証が有効かどうか
    isOtpEnabled: state => {
      return state.currentUser?.isOtpEnabled === true
    },
    // パスワードサインインが有効かどうか
    isPasswordSigninEnabled: state => {
      return state.currentUser?.isPasswordSigninEnabled === true
    },
    // パスキーが登録されているかどうか
    hasPasskeys: state => {
      return state.currentUser?.passkeys && state.currentUser.passkeys.length > 0
    },
    // ユーザーのアイコンURLを取得
    userIconUrl: state => {
      return state.currentUser?.iconPath || null
    },

    // 現在のユーザー名を取得（チェック用）
    currentUserShortName: state => {
      if (!state.currentUser) return '名前未設定'
      return state.currentUser.shortName || `${state.currentUser.lastName} ${state.currentUser.firstName}`
    },

    // 現在のユーザーIDを取得
    currentUserId: state => {
      return state.currentUser?.id || null
    },

    // 文字幅を考慮した省略表示（汎用メソッド）
    getTruncatedUserName:
      state =>
      (userName, maxCharWidth = 16) => {
        if (!userName) return '不明なユーザー'

        // 文字幅を計算（全角=2、半角=1）
        const getDisplayWidth = str => {
          return [...str].reduce((width, char) => {
            return width + (char.match(/[^\x00-\x7F]/) ? 2 : 1)
          }, 0)
        }

        if (getDisplayWidth(userName) < maxCharWidth) {
          return userName
        }

        // 省略処理
        let truncated = ''
        let width = 0
        const targetWidth = maxCharWidth - 2 // "..."の分を考慮

        for (const char of userName) {
          const charWidth = char.match(/[^\x00-\x7F]/) ? 2 : 1
          if (width + charWidth > targetWidth) break
          truncated += char
          width += charWidth
        }

        return truncated + '...'
      },

    // 認証メール再送信ボタンの表示条件
    shouldShowResendEmailButton: state => item => {
      const isAdmin = state.currentUser && (state.currentUser.role === 'manager' || state.currentUser.role === 'root')
      return !item.lastLoginAt && !item.isVerified && isAdmin
    },

    // メールアドレス変更ボタンの表示条件
    shouldShowChangeEmailButton: state => item => {
      const isAdmin = state.currentUser && (state.currentUser.role === 'manager' || state.currentUser.role === 'root')
      return item.isVerified && isAdmin
    },
  },
  persist: {
    paths: ['currentUser'], // 現在のユーザー情報を永続化
  },
})
